// Cart lives client-side only (localStorage). At checkout, the cart
// is sent to /checkout/create-payment-intent which reserves stock and
// returns a Stripe client_secret.
//
// Identity: cart lines are keyed by SKU. Adding the same SKU with the
// same personalization increments qty; same SKU with different
// personalization is a separate line.
import React, { createContext, useContext, useEffect, useReducer } from 'react';
import type { CartLine, CartLinePersonalization } from '../types';

const STORAGE_KEY = 'bleums.cart.v1';

interface CartState {
  lines: CartLine[];
}

type CartAction =
  | { type: 'ADD'; line: CartLine }
  | { type: 'UPDATE_QTY'; lineKey: string; qty: number }
  | { type: 'REMOVE'; lineKey: string }
  | { type: 'CLEAR' }
  | { type: 'INIT'; state: CartState };

interface CartContextType {
  lines: CartLine[];
  subtotalCents: number;
  itemCount: number;
  addLine: (line: Omit<CartLine, 'qty'> & { qty?: number }) => void;
  updateQty: (lineKey: string, qty: number) => void;
  removeLine: (lineKey: string) => void;
  clear: () => void;
  // A stable per-line key built from sku + personalization. Use this
  // when rendering and when updating/removing lines.
  lineKey: (line: Pick<CartLine, 'sku' | 'personalization'>) => string;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const initial: CartState = { lines: [] };

const personalizationKey = (p?: CartLinePersonalization): string => {
  if (!p) return '';
  return Object.keys(p).sort().map(k => `${k}=${p[k]}`).join('|');
};

// Notes are part of a line's identity, not decoration on it. Two of the
// same product with different names on them -- "one for Emma, one for
// Sophie" -- are two different things to make, and merging them into a
// single line of qty 2 would keep one name and silently drop the other.
const lineKey = (
  line: Pick<CartLine, 'sku' | 'personalization' | 'notes'>
): string =>
  `${line.sku}::${personalizationKey(line.personalization)}::${line.notes ?? ''}`;

const reducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'INIT':
      return action.state;
    case 'ADD': {
      const key = lineKey(action.line);
      const existing = state.lines.findIndex(l => lineKey(l) === key);
      if (existing >= 0) {
        const next = [...state.lines];
        next[existing] = { ...next[existing], qty: next[existing].qty + action.line.qty };
        return { lines: next };
      }
      return { lines: [...state.lines, action.line] };
    }
    case 'UPDATE_QTY': {
      const lines = state.lines.flatMap(l => {
        if (lineKey(l) !== action.lineKey) return [l];
        if (action.qty <= 0) return [];
        return [{ ...l, qty: action.qty }];
      });
      return { lines };
    }
    case 'REMOVE':
      return { lines: state.lines.filter(l => lineKey(l) !== action.lineKey) };
    case 'CLEAR':
      return { lines: [] };
    default:
      return state;
  }
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initial);

  // Hydrate from localStorage on first mount.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) dispatch({ type: 'INIT', state: JSON.parse(raw) });
    } catch {
      // Corrupted cart — fall back to empty rather than crashing.
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Persist on any change.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // localStorage full or unavailable — silently ignore.
    }
  }, [state]);

  const subtotalCents = state.lines.reduce((sum, l) => sum + l.unit_price_cents * l.qty, 0);
  const itemCount = state.lines.reduce((sum, l) => sum + l.qty, 0);

  const value: CartContextType = {
    lines: state.lines,
    subtotalCents,
    itemCount,
    addLine: (line) => dispatch({ type: 'ADD', line: { qty: 1, ...line } as CartLine }),
    updateQty: (key, qty) => dispatch({ type: 'UPDATE_QTY', lineKey: key, qty }),
    removeLine: (key) => dispatch({ type: 'REMOVE', lineKey: key }),
    clear: () => dispatch({ type: 'CLEAR' }),
    lineKey,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
};
